import { createContext, useContext, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import type { PartBotResultJson, RunesCallout } from '@frontend/api';
import { noop } from '@frontend/utils';

import { featureFlagKeys } from 'modules/common/drawers/featureFlagDrawer/const/featureFlagKeys';
import { SUGGESTION_ANNOTATION_COMMAND_TYPES } from 'modules/common/viewer/pdfViewer/annotationCommands';
import { suggestionAnnotations } from 'modules/common/viewer/pdfViewer/data/actions';

import { useFeatureFlagValue } from 'store/session/hooks';

type CreateRuleActionData = {
  canCreateRule: boolean;
  processPartBotResult: (
    partBotResult: PartBotResultJson,
  ) => RuleCreationPayload | null;
  createRuleAction: (ruleCreationPayload: RuleCreationPayload) => void;
};

const DEFAULT_ACTION_DATA: CreateRuleActionData = {
  canCreateRule: false,
  processPartBotResult: () => null,
  createRuleAction: noop,
};

const CreateRuleActionDataContext =
  createContext<CreateRuleActionData>(DEFAULT_ACTION_DATA);

type CreateRuleActionDataProviderProps = {
  children: React.ReactNode;
};

export type RuleCreationPayload = {
  rawCallout: RunesCallout | null;
  selectedText: string | null;
};

export const CreateRuleActionDataProvider = ({
  children,
}: CreateRuleActionDataProviderProps) => {
  const dispatch = useDispatch();
  const isRequirementsReviewEnabled = useFeatureFlagValue(
    featureFlagKeys.isRequirementsReviewEnabled,
  );

  const processPartBotResult = useCallback(
    (partBotResult: PartBotResultJson): RuleCreationPayload | null => {
      if (!isRequirementsReviewEnabled || !partBotResult?.canAuthorRule) {
        return null;
      }

      return {
        rawCallout: partBotResult.rawCallout ?? null,
        selectedText: partBotResult.value ?? null,
      };
    },
    [isRequirementsReviewEnabled],
  );

  const createRuleAction = useCallback(
    (ruleCreationPayload: RuleCreationPayload) => {
      dispatch(
        suggestionAnnotations.queueCommands({
          commands: [
            {
              type: SUGGESTION_ANNOTATION_COMMAND_TYPES.OPEN_RULE_CREATION,
              payload: ruleCreationPayload,
            },
          ],
        }),
      );
    },
    [dispatch],
  );

  const value = useMemo<CreateRuleActionData>(
    () => ({
      processPartBotResult,
      createRuleAction,
      canCreateRule: isRequirementsReviewEnabled,
    }),
    [isRequirementsReviewEnabled, processPartBotResult, createRuleAction],
  );

  return (
    <CreateRuleActionDataContext.Provider value={value}>
      {children}
    </CreateRuleActionDataContext.Provider>
  );
};

export const useCreateRuleActionData = (): CreateRuleActionData =>
  useContext(CreateRuleActionDataContext);
